import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Link2, Linkedin, Twitter } from 'lucide-react';
import { toast } from 'sonner';
import SEO from '../components/SEO';
import { Reveal, Eyebrow } from '../components/Reveal';
import api from '../lib/api';

export default function InsightDetail() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/insights/${slug}`).then((r) => setData(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-6 bg-brand-offwhite px-5 pt-24" data-testid="insight-not-found">
        <h1 className="font-heading text-3xl font-bold text-brand-black">Article not found</h1>
        <Link to="/insights" className="flex items-center gap-2 font-heading text-sm font-bold text-brand-deepgreen"><ArrowLeft size={16} /> Back to Insights</Link>
      </div>
    );
  }

  if (!data) {
    return <div className="flex min-h-screen items-center justify-center bg-brand-offwhite pt-24"><div className="h-10 w-10 animate-spin rounded-full border-2 border-brand-green border-t-transparent" /></div>;
  }

  const a = data.article;
  const shareUrl = `https://www.insightsstrategy.in/insights/${a.slug}`;

  const copyLink = () => {
    navigator.clipboard?.writeText(shareUrl);
    toast.success('Link copied to clipboard');
  };

  return (
    <>
      <SEO title={`${a.title} | INSIGHTS STRATEGY`} description={(a.summary || a.title).slice(0, 155)} path={`/insights/${slug}`} />
      <section className="bg-brand-deep" data-testid="insight-hero">
        <div className="mx-auto max-w-4xl px-5 pb-16 pt-40 lg:px-8">
          <Link to="/insights" className="flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-white/50 transition-colors hover:text-brand-green" data-testid="insight-back">
            <ArrowLeft size={14} /> All Insights
          </Link>
          <p className="mt-8 text-xs font-bold uppercase tracking-[0.25em] text-brand-green">{a.category}</p>
          <h1 className="mt-4 font-heading text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-5xl">{a.title}</h1>
          <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-white/55">
            {a.author && <span>{a.author}</span>}
            {a.published_at && <span>{new Date(a.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
            <span>{Math.max(1, Math.round((a.content || '').split(/\s+/).length / 200))} min read</span>
          </div>
        </div>
      </section>

      {a.cover_image && (
        <div className="mx-auto max-w-4xl px-5 lg:px-8">
          <img src={a.cover_image} alt={a.title} className="aspect-[21/9] w-full rounded-2xl object-cover" data-testid="insight-cover" />
        </div>
      )}

      <section className="bg-brand-offwhite">
        <div className="mx-auto max-w-4xl px-5 py-16 lg:px-8 lg:py-20">
          {a.summary && <p className="border-l-2 border-brand-green pl-6 font-heading text-lg font-semibold leading-relaxed text-brand-black">{a.summary}</p>}
          <div className="mt-10 whitespace-pre-wrap text-base leading-[1.9] text-brand-black/80" data-testid="insight-content">{a.content}</div>

          <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-brand-grey pt-8" data-testid="insight-share">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-brand-body">Share</span>
            <button onClick={copyLink} data-testid="share-copy-link" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-grey text-brand-black transition-colors hover:border-brand-green hover:text-brand-deepgreen" aria-label="Copy link">
              <Link2 size={16} />
            </button>
            <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-grey text-brand-black transition-colors hover:border-brand-green hover:text-brand-deepgreen" aria-label="Share on LinkedIn">
              <Linkedin size={16} />
            </a>
            <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(a.title)}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="flex h-10 w-10 items-center justify-center rounded-full border border-brand-grey text-brand-black transition-colors hover:border-brand-green hover:text-brand-deepgreen" aria-label="Share on X">
              <Twitter size={16} />
            </a>
          </div>
        </div>
      </section>

      {data.related?.length > 0 && (
        <section className="bg-white" data-testid="insight-related">
          <div className="mx-auto max-w-7xl px-5 py-20 lg:px-8">
            <Eyebrow>Related Articles</Eyebrow>
            <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {data.related.map((r) => (
                <Reveal key={r.insight_id}>
                  <Link to={`/insights/${r.slug}`} className="group block h-full rounded-2xl border border-brand-grey bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_50px_rgba(11,13,11,0.08)]" data-testid={`related-insight-${r.slug}`}>
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-brand-deepgreen">{r.category}</span>
                    <h3 className="mt-3 font-heading text-lg font-bold leading-snug text-brand-black">{r.title}</h3>
                    <span className="mt-4 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.15em] text-brand-deepgreen">Read <ArrowUpRight size={14} /></span>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}
